/**
 * Frontend API Service
 *
 * Handles all API calls to the snpod backend
 */

const API_BASE = import.meta.env.DEV ? '' : '';
const ENV_API_KEY = import.meta.env.VITE_SNPOD_API_KEY || '';

/**
 * Get API key from URL parameter or fall back to environment variable
 */
function getApiKey(): string {
  // Check URL parameter first (for multi-tenant support)
  const urlParams = new URLSearchParams(window.location.search);
  const urlApiKey = urlParams.get('api_key');

  if (urlApiKey) {
    return urlApiKey;
  }

  // Fall back to environment variable
  return ENV_API_KEY;
}

// =============================================================================
// Session Token Management (in-memory only, NOT localStorage)
// =============================================================================

let sessionToken: string | null = null;
let sessionExpiresAt: number | null = null;

export function setSessionToken(token: string, expiresAt: string): void {
  sessionToken = token;
  sessionExpiresAt = new Date(expiresAt).getTime();
  console.log('[Session] Token set, expires:', expiresAt);
}

export function clearSessionToken(): void {
  sessionToken = null;
  sessionExpiresAt = null;
  console.log('[Session] Token cleared');
}

export function hasValidSession(): boolean {
  if (!sessionToken || !sessionExpiresAt) return false;
  return Date.now() < sessionExpiresAt;
}

// =============================================================================
// Session Creation
// =============================================================================

export interface SNSessionContext {
  userId: string;
  userName?: string;
  userEmail?: string;
  ticketSysId?: string;
  table?: string;
}

export interface SessionResponse {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    name: string;
  };
}

/**
 * Create a session by providing SN user identity.
 * Called after receiving SN context via URL params.
 */
export async function createSession(snContext: SNSessionContext): Promise<SessionResponse> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const apiKey = getApiKey();
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  const url = `${API_BASE}/api/lite/session`;
  console.log('[Session] Creating session:', { url, userId: snContext.userId, hasApiKey: !!apiKey });

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(snContext),
    });
  } catch (fetchErr) {
    console.error('[Session] Fetch failed (network error):', fetchErr);
    throw fetchErr;
  }

  console.log('[Session] Response:', res.status, res.statusText);

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.error('[Session] Server error:', data);
    throw new Error(data.error || data.message || `Session creation failed: ${res.status}`);
  }

  const result: SessionResponse = await res.json();
  setSessionToken(result.token, result.expiresAt);
  return result;
}

// =============================================================================
// API Call Infrastructure
// =============================================================================

/**
 * Common headers for API requests
 */
export function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (hasValidSession()) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  } else {
    // Fallback for session creation endpoint only
    const apiKey = getApiKey();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
  }

  return headers;
}

/**
 * API response types
 */
export interface CallerInfo {
  sys_id: string | null;
  name: string | null;
  email?: string;
  title?: string;
  phone?: string;
}

export interface CompanyInfo {
  sys_id: string | null;
  name: string | null;
}

export interface JournalEntry {
  sys_id: string;
  type: 'work_note' | 'comment';
  value: string;
  created_on: string;
  created_by: string;
}

export interface RecordContext {
  sys_id: string;
  table: string;
  number: string;
  short_description: string;
  description: string;
  state: string;
  state_display: string;
  priority: string;
  priority_display: string;
  urgency: string | number;
  impact: string | number;
  category: string | null;
  subcategory: string | null;
  assignment_group: { sys_id: string; name: string } | null;
  assigned_to: { sys_id: string; name: string } | null;
  caller: CallerInfo | null;
  company: CompanyInfo | null;
  journal_entries: JournalEntry[];
  sys_created_on: string;
  sys_updated_on: string;
  resolved_at?: string;
  close_notes?: string;
}

/**
 * Metric score with explanation
 */
export interface MetricScore {
  score: number;
  explanation: string;
}

export interface SkillsMetricScore extends MetricScore {
  required: string[];
}

export interface VipMetricScore extends MetricScore {
  isVip: boolean;
  reason: string;
}

export interface MultiUserEventMetricScore extends MetricScore {
  isMultiUser: boolean;
  affectedScope: string;
}

export interface ResponseQualityMetricScore extends MetricScore {
  strengths: string[];
  improvements: string[];
}

export interface TicketMetrics {
  clarity: MetricScore;
  sentiment: MetricScore;
  empathy: MetricScore;
  acumen: MetricScore;
  cadence: MetricScore;
  disruption: MetricScore;
  age: MetricScore;
  priority: MetricScore;
  chronic: MetricScore;
  skills: SkillsMetricScore;
  vip: VipMetricScore;
  multiUserEvent: MultiUserEventMetricScore;
  responseQuality: ResponseQualityMetricScore;
}

export interface SalesOpportunityData {
  detected: boolean;
  confidence: number;
  type: string;
  summary: string;
  signals: string[];
}

export interface AnalysisResult {
  summary: string;
  sentiment: {
    overall: 'positive' | 'neutral' | 'negative';
    confidence: number;
    metrics: TicketMetrics;
    escalation: {
      score: number;
      recommended: boolean;
      factors: string[];
    };
  };
  salesOpportunity?: SalesOpportunityData;
  suggestedActions: string[];
  _meta?: {
    model: string;
    tokens: number;
    responseTimeMs: number;
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  message: string;
  _meta?: {
    model: string;
    tokens: number;
    responseTimeMs: number;
  };
}

/**
 * API Error class
 */
export class ApiError extends Error {
  status: number;
  details?: string;

  constructor(message: string, status: number, details?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Fetch record context from ServiceNow
 */
export async function fetchRecordContext(sysId: string, table = 'incident'): Promise<RecordContext> {
  const url = `${API_BASE}/api/lite/context?sys_id=${encodeURIComponent(sysId)}&table=${encodeURIComponent(table)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch record context',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Fetch AI analysis for a record
 * Sends the full record context to the AI for analysis
 */
export async function fetchAnalysis(record: RecordContext): Promise<AnalysisResult> {
  const url = `${API_BASE}/api/ai/analyze`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ record }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch analysis',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Send a chat message
 * Sends the full record context along with conversation history
 */
export async function sendChatMessage(
  record: RecordContext,
  messages: ChatMessage[]
): Promise<ChatResponse> {
  const url = `${API_BASE}/api/ai/chat`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      record,
      messages,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to send message',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Knowledge Base types
 */
export interface KBArticle {
  sys_id: string;
  number: string;
  title: string;
  snippet: string;
  category: string | null;
  url: string;
}

export interface KBSuggestion extends KBArticle {
  relevance: string;
}

export interface KBArticleDetail {
  sys_id: string;
  number: string;
  title: string;
  content: string;
  category: string | null;
  author: string | null;
  published: string | null;
  views: number;
  url: string;
}

/**
 * Search knowledge base articles
 */
export async function searchKB(query: string, limit = 10): Promise<{ query: string; count: number; articles: KBArticle[] }> {
  const url = `${API_BASE}/api/kb/search?q=${encodeURIComponent(query)}&limit=${limit}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to search knowledge base',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Get AI-powered KB article suggestions for a record
 */
export async function getKBSuggestions(record: RecordContext): Promise<{ suggestions: KBSuggestion[] }> {
  const url = `${API_BASE}/api/kb/suggestions`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ record }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to get KB suggestions',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Get a single KB article by sys_id
 */
export async function getKBArticle(sysId: string): Promise<KBArticleDetail> {
  const url = `${API_BASE}/api/kb/article?sys_id=${encodeURIComponent(sysId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch article',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Similar Records types
 */
export interface SimilarRecord {
  sys_id: string;
  number: string;
  short_description: string;
  state: string;
  resolution: string | null;
  created_on: string;
  url: string;
}

/**
 * Find similar records
 */
export async function findSimilarRecords(record: RecordContext, limit = 5): Promise<{ count: number; records: SimilarRecord[] }> {
  const url = `${API_BASE}/api/records/similar`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ record, limit }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to find similar records',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Add a note to a record (work note, comment, or resolution)
 */
export async function addWorkNote(
  sysId: string,
  table: string,
  note: string,
  noteType?: 'work_note' | 'comment' | 'resolution'
): Promise<{ success: boolean }> {
  const url = `${API_BASE}/api/records/note`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ sys_id: sysId, table, note, noteType }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to add work note',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Add a customer comment to a record
 */
export async function addComment(sysId: string, table: string, comment: string): Promise<{ success: boolean }> {
  const url = `${API_BASE}/api/records/comment`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ sys_id: sysId, table, comment }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to add comment',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Time Entry types
 */
export interface TimeEntryRequest {
  sys_id: string;
  table: string;
  hours: number;
  minutes: number;
  notes?: string;
  billable?: boolean;
}

export interface TimeEntryResponse {
  success: boolean;
  duration: string;
  seconds: number;
  billable: boolean;
  notes: string | null;
}

/**
 * Log time against a record
 */
export async function logTimeEntry(request: TimeEntryRequest): Promise<TimeEntryResponse> {
  const url = `${API_BASE}/api/records/time-entry`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to log time',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Response Generator types
 */
export type ResponseTone = 'professional' | 'friendly' | 'technical';
export type ResponseLength = 'concise' | 'detailed';

export interface GeneratedResponse {
  response: string;
  tone: ResponseTone;
  length: ResponseLength;
  _meta?: {
    model: string;
    tokens: number;
    responseTimeMs: number;
  };
}

/**
 * Generate a customer response for a record
 */
export async function generateResponse(
  record: RecordContext,
  tone: ResponseTone = 'professional',
  length: ResponseLength = 'concise',
  customInstructions?: string
): Promise<GeneratedResponse> {
  const url = `${API_BASE}/api/ai/generate-response`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      record,
      tone,
      length,
      customInstructions,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to generate response',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Quick Actions types
 */
export type ActionType = 'assign' | 'resolve' | 'close' | 'reopen' | 'update_priority' | 'update_state';

export interface ActionRequest {
  action: ActionType;
  sys_id: string;
  table: string;
  assigned_to?: string;
  assignment_group?: string;
  close_notes?: string;
  close_code?: string;
  priority?: string;
  state?: string;
}

/**
 * Perform a quick action on a record
 */
export async function performAction(request: ActionRequest): Promise<{ success: boolean }> {
  const url = `${API_BASE}/api/records/actions`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Action failed',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * SLA types
 */
export interface SLAInfo {
  sys_id: string;
  name: string;
  stage: string;
  has_breached: boolean;
  percentage: number;
  start_time: string;
  end_time: string;
  duration: string;
  time_left: string;
  business_time_left: string;
}

/**
 * Get SLA information for a record
 */
export async function fetchSLAs(sysId: string, table = 'incident'): Promise<{ slas: SLAInfo[] }> {
  const url = `${API_BASE}/api/records/sla?sys_id=${encodeURIComponent(sysId)}&table=${encodeURIComponent(table)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch SLAs',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Attachment types
 */
export interface Attachment {
  sys_id: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  size_display: string;
  created_on: string;
  created_by: string;
  download_url: string;
  thumbnail_url?: string;
}

/**
 * Get attachments for a record
 */
export async function fetchAttachments(sysId: string, table = 'incident'): Promise<{ attachments: Attachment[] }> {
  const url = `${API_BASE}/api/records/attachments?sys_id=${encodeURIComponent(sysId)}&table=${encodeURIComponent(table)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch attachments',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Entitlement types
 */
export interface Entitlement {
  sys_id: string;
  name: string;
  type: string;
  total_units: number;
  remaining_units: number;
  percentage_used: number;
  channel: string;
  support_level: string;
  active: boolean;
  end_date: string;
}

export interface Contract {
  sys_id: string;
  name: string;
  number: string;
  account: string;
  status: string;
  start_date: string;
  end_date: string;
  support_level: string;
  entitlements: Entitlement[];
}

export interface EntitlementResponse {
  contracts: Contract[];
  summary: {
    total_entitlements: number;
    active_contracts: number;
    support_level: string;
    has_active_support: boolean;
  };
}

/**
 * Get entitlements for a customer account
 */
export async function fetchEntitlements(accountId: string): Promise<EntitlementResponse> {
  const url = `${API_BASE}/api/records/entitlements?account_id=${encodeURIComponent(accountId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch entitlements',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Documentation Ticket types
 */
export interface DocumentationTicketRequest {
  source_sys_id: string;
  source_number: string;
  source_table: string;
  short_description: string;
  description: string;
  category?: string;
  company_sys_id?: string;
  company_name?: string;
  assignment_group_sys_id?: string;
  chat_history?: ChatMessage[];
}

export interface DocumentationTicketResponse {
  success: boolean;
  ticket: {
    sys_id: string;
    number: string;
    table: string;
    url: string;
  };
  source: {
    sys_id: string;
    number: string;
    table: string;
  };
  itglue_doc?: {
    doc_id: string;
    doc_url: string;
  } | null;
  ai_generated?: boolean;
}

/**
 * Create a documentation ticket linked to the source ticket
 */
export async function createDocumentationTicket(request: DocumentationTicketRequest): Promise<DocumentationTicketResponse> {
  const url = `${API_BASE}/api/records/create-documentation`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to create documentation ticket',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Sales Ticket types
 */
export interface SalesTicketRequest {
  source_sys_id: string;
  source_number: string;
  source_table: string;
  company_sys_id?: string;
  company_name?: string;
  contact_name?: string;
  opportunity_summary: string;
  opportunity_type: string;
  signals: string[];
  assignment_group_sys_id?: string;
}

export interface SalesTicketResponse {
  success: boolean;
  ticket: {
    sys_id: string;
    number: string;
    table: string;
    url: string;
  };
}

/**
 * Create a sales opportunity ticket in ServiceNow
 */
export async function createSalesTicket(request: SalesTicketRequest): Promise<SalesTicketResponse> {
  const url = `${API_BASE}/api/records/create-sales-ticket`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to create sales ticket',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * ScalePad Equipment Alert types
 */
export interface ScalePadAlertDevice {
  name: string;
  type: string;
  manufacturer: string | null;
  model: string | null;
  age: number | null;
  threshold: number;
  warrantyStatus: 'expired' | 'expiring_soon' | 'active';
  warrantyExpiry: string | null;
  lifecycleStatus: string;
}

export interface ScalePadAlertData {
  hasAlerts: boolean;
  severity: 'critical' | 'high' | 'medium' | 'none';
  matchedDeviceTypes: string[];
  devices: ScalePadAlertDevice[];
  totalCompanyDevices: number;
  totalAlertDevices: number;
  totalRelevantDevices: number;
  thresholds: Record<string, number>;
}

/**
 * Fetch ScalePad equipment lifecycle alerts for a company
 */
export async function fetchScalePadAlerts(companySysId: string, ticketSummary?: string): Promise<ScalePadAlertData> {
  const params = new URLSearchParams({ companyId: companySysId });
  if (ticketSummary) params.set('ticketSummary', ticketSummary);

  const url = `${API_BASE}/api/scalepad/alerts?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(
      error.message || 'Failed to fetch equipment alerts',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Upload a file via multipart FormData (WS4)
 * Used for binary audio upload — no base64 overhead.
 */
export async function apiUpload(
  path: string,
  formData: FormData
): Promise<any> {
  const url = `${API_BASE}${path}`;

  const headers: HeadersInit = {};
  if (hasValidSession()) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  } else {
    const apiKey = getApiKey();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
  }
  // Do NOT set Content-Type — browser sets it with boundary for multipart

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new ApiError(
      error.message || 'Upload failed',
      response.status,
      error.details
    );
  }

  return response.json();
}

/**
 * Mock data for development when API is not available
 */
export function getMockContext(sysId: string, table: string): RecordContext {
  return {
    sys_id: sysId,
    table,
    number: table === 'incident' ? 'INC0010001' : 'CS0010001',
    short_description: 'Sample record - Connect to ServiceNow for real data',
    description: 'This is a mock record for development purposes. Connect your ServiceNow developer instance to see real data.',
    state: '2',
    state_display: 'In Progress',
    priority: '3',
    priority_display: 'Moderate',
    urgency: '2',
    impact: '2',
    category: 'Software',
    subcategory: 'Email',
    assignment_group: { sys_id: 'abc123', name: 'IT Support' },
    assigned_to: { sys_id: 'def456', name: 'John Technician' },
    caller: {
      sys_id: 'user123',
      name: 'Jane Customer',
      email: 'jane@example.com',
      title: 'Manager',
    },
    company: {
      sys_id: 'company123',
      name: 'Acme Corporation',
    },
    journal_entries: [
      {
        sys_id: 'j1',
        type: 'work_note',
        value: 'Initial investigation started. Checking email server logs.',
        created_on: new Date(Date.now() - 3600000).toISOString(),
        created_by: 'admin',
      },
      {
        sys_id: 'j2',
        type: 'comment',
        value: 'Thank you for looking into this issue.',
        created_on: new Date(Date.now() - 1800000).toISOString(),
        created_by: 'jane.customer',
      },
    ],
    sys_created_on: new Date(Date.now() - 86400000).toISOString(),
    sys_updated_on: new Date().toISOString(),
  };
}
